/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, {useEffect, useRef, useState} from 'react';

// material
import {
    Group,
    Control,
    Icon,
    DayPickerDropDown,
    SingleSelectDropDown, Button,
} from '@insticator/insticator-ui'; // iui-material
import AutoCompleteTextField from "../../../../../material/autofilltext/autofilltext";
import Switch from '@mui/material/Switch';

// redux
import {fetchAccountUsers} from "../../../../../state/thunks/account";
import {useDispatch, useSelector} from "react-redux";

// utils
import {validation} from "../../../../../utils/validation";

// components
import SectionGroupDescription
    from "../../../../../material/expandablesection/sectiongroupdescription/sectiongroupdescription";
import SectionGroup from "../../../../../material/expandablesection/sectiongroup/sectiongroup";
import Stack from "../../../../../material/stack/stack";

// styles
import injectSheet from 'react-jss';
import styles from './deliverysettings_styles';

// static
import {
    END_DATE,
    FREQUENCY,
        FREQUENCY_OPTIONS,
    IS_SCHEDULED,
    RECIPIENTS,
    SCHEDULE,
    START_DATE,
    RECIPIENTS_ERROR_TEXT,
    EMAIL_ERROR_TEXT, EMAIL_DUPLICATED_ERROR_TEXT,
    FREQUENCY_MESSAGE_MONTHLY,
    FREQUENCY_MESSAGE_WEEKLY,
    FREQUENCY_MESSAGE_DAILY, TOO_MANY_RECIPIENTS_ERROR_TEXT
} from "../consts";
import {updateReportForm} from "../../../../../state/slices/report";
import {chartTableColors} from "../../../../../utils/charts";
import Information from "../../../../../material/information/information";



/* ========== ~~~~~~~~~~ FREQUENCY FORM ~~~~~~~~~~ ========== */
export default injectSheet(styles)((props) => {
    const formValues = useSelector((state) => state.report[props.uuid]);
    const accountUUID = useSelector((state) => state.session.accountUUID);
    const users = useSelector((state) => state.account.accountUsers);

    const [formErrorText, setFormErrorText] = useState({});
    const [emailInput, setEmailInput] = useState('');
    const [clear, setClear] = useState();

    const scheduleValues = formValues[SCHEDULE];
    const recipients = scheduleValues[RECIPIENTS];
    const isScheduled = formValues[IS_SCHEDULED];
    const [lastWeeklyDate, setLastWeeklyDate] = useState(scheduleValues[START_DATE]);
    const [canScheduleReport, setCanScheduleReport] = useState(formValues.dateRange !== 'CUSTOM');
    const [reportScheduleToggle, setReportScheduleToggle] = useState(!!isScheduled);


    const dispatch = useDispatch();

    const handleOnChange = newValues => {
        dispatch(updateReportForm({...formValues, ...newValues}));
    }

    useEffect(() => {
        if (scheduleValues[FREQUENCY] === FREQUENCY_MESSAGE_WEEKLY) setLastWeeklyDate(scheduleValues[START_DATE])
    }, [scheduleValues[FREQUENCY]])
    useEffect(() => {
        if (clear === ' ') setClear(undefined);
    }, [clear])
    useEffect(() => {
        dispatch(fetchAccountUsers(accountUUID));
    }, [])
    useEffect(() => {
        if (!(recipients || []).length) setFormErrorText({...formErrorText, [RECIPIENTS]: RECIPIENTS_ERROR_TEXT });
        else setFormErrorText({...formErrorText, [RECIPIENTS]: ''});
    }, [recipients])


    useEffect(() => {
        if (formValues.dateRange === 'CUSTOM') {
            setCanScheduleReport(false);
            if(isScheduled) {
                handleSetIsScheduled();
            }
        }
        else {
            setCanScheduleReport(true);
        }
    }, [formValues])


    const handleSetIsScheduled = () => {
        const newValues = { [IS_SCHEDULED]: !isScheduled };
        setReportScheduleToggle(!isScheduled);
        if (scheduleValues && !scheduleValues[START_DATE] && !scheduleValues[END_DATE]) {
            newValues[SCHEDULE] = {
                ...scheduleValues,
                [START_DATE]: fromDatesMappedByDays[0].value,
                [END_DATE]: getEndofMonth(new Date()).toISOString().slice(0, 10),
            }
            if (!newValues[SCHEDULE][FREQUENCY] || newValues[SCHEDULE][FREQUENCY] === 'ONCE') newValues[SCHEDULE][FREQUENCY] = 'WEEKLY';
        }
        handleOnChange(newValues);

    }


    const handleSelect = val => {
        if (emailValidation.test(val)) {
            let newSettings = {...scheduleValues}
            const newValues = [...(new Set([...recipients, val]))];
            newSettings[RECIPIENTS] = newValues
            if (recipients.includes(val)) setFormErrorText({...formErrorText, [RECIPIENTS]: EMAIL_DUPLICATED_ERROR_TEXT });
            else if (recipients.length === 10) setFormErrorText({...formErrorText, [RECIPIENTS]: TOO_MANY_RECIPIENTS_ERROR_TEXT });
            else handleOnChange({[SCHEDULE]: newSettings})
            setClear(' ');
        } else setFormErrorText({...formErrorText, [RECIPIENTS]: EMAIL_ERROR_TEXT})
    }

    const handleRemoveRecipient = email => {
        let newSettings = {...scheduleValues}
        const newValues = recipients.filter(r => r !== email)
        newSettings[RECIPIENTS] = newValues
        handleOnChange({[SCHEDULE]: newSettings})
    }

    const emailValidation = new RegExp(validation.email.test);

    const handleBlur = val => setEmailInput(val);
    const handleKeyDown = e => {
        const value = e.target.value;
        setEmailInput(value);
        if (e.key === 'Enter') handleSelect(e.target.value)
    }

    const handleFrequencyChange = val => {
        let newSettings = {...scheduleValues}
        let date = new Date()
        let startDate;
        switch (val) {
            case 'DAILY':
                date.setDate(date.getDate() + 1);
                startDate = date.toISOString().split('T')[0]
                break;
            case 'WEEKLY':
                date.setDate(date.getDate() + 1);
                startDate = date.toISOString().split('T')[0];
                setLastWeeklyDate(startDate);
                break;
            case 'MONTHLY':
                date = new Date(date.getFullYear(), date.getMonth(), 2);
                startDate = date.toISOString().split('T')[0]
                break;
            default:
                break;
        }
        newSettings[FREQUENCY] = val
        newSettings[START_DATE] = startDate
        handleOnChange({[SCHEDULE]: newSettings})
    }

    const handleFromDateChange = val => {
        let newSettings = {...scheduleValues}
        newSettings[START_DATE] = val.value
        handleOnChange({[SCHEDULE]: newSettings})
        setLastWeeklyDate(val.value)
    }

    const handleToDateChange = val => {
        let newSettings = {...scheduleValues}
        newSettings[END_DATE] = val
        handleOnChange({[SCHEDULE]: newSettings})
    }

    const convertDayToDate = (target) => {
        const today = new Date()
        const dayIdx = new Date().getDay();
        let daysAdded;

        if (dayIdx < target) daysAdded = target - dayIdx;
        else daysAdded = 7 + (target - dayIdx);

        const closest = today.getDate() + daysAdded;
        return new Date(today.setDate(closest)).toISOString().substring(0, 10);
    }

    const getEndofMonth = date => {
        let endOfMonth = new Date(date);
        // Set the date to the first day of the next month
        endOfMonth.setMonth(endOfMonth.getMonth() + 1);
        endOfMonth.setDate(1);
        // Subtract one day to get the last day of the current month
        endOfMonth.setDate(endOfMonth.getDate() - 1);
        return endOfMonth;
    }

    // these need to be computed on the fly
    const fromDatesMappedByDays = [
        {"name": "Monday", "value": convertDayToDate(1)},
        {"name": "Tuesday", "value": convertDayToDate(2)},
        {"name": "Wednesday", "value": convertDayToDate(3)},
        {"name": "Thursday", "value": convertDayToDate(4)},
        {"name": "Friday", "value": convertDayToDate(5)},
        {"name": "Saturday", "value": convertDayToDate(6)},
        {"name": "Sunday", "value": convertDayToDate(0)},
    ];

    const getDisabledDates = () => [{
        before: new Date(scheduleValues[START_DATE] || fromDatesMappedByDays[0].value)
    }]

    const getDateRangeMessage = () => {
        switch(scheduleValues[FREQUENCY]) {
            case 'MONTHLY':
                return `${FREQUENCY_MESSAGE_MONTHLY}`
            case 'WEEKLY':
                const day = fromDatesMappedByDays.find(d => d.value === scheduleValues[START_DATE]);
                return `${FREQUENCY_MESSAGE_WEEKLY[0]} ${day?.name} ${FREQUENCY_MESSAGE_WEEKLY[1]} ${scheduleValues[END_DATE]}.`
            case 'DAILY':
                return `${FREQUENCY_MESSAGE_DAILY} ${scheduleValues[END_DATE]}.`
            default:
                return ''
        }
    }

    const convertRecipientsToObjectArr = (recipients) => {
        return recipients.map((recipient) => ({value : recipient}))
    }

    return (
        <div className={props.classes.deliverySettings}>
            <SectionGroup row>
                <Switch
                    disabled={!canScheduleReport}
                    onChange={handleSetIsScheduled}
                    checked={reportScheduleToggle}/>
                <SectionGroupDescription
                    title={'Scheduled Email Report'}
                    description={'Enable sending your report to colleagues on a daily, weekly or monthly basis. '}
                    titleClassName={`scheduledEmailTitle ${!canScheduleReport ? 'disableScheduledEmailTitle' : ''}`}
                    descriptionClassName={`scheduleEmailReportDescription ${!canScheduleReport ? 'disableScheduledEmailReportDescription' : '' }`}
                />
            </SectionGroup>
            {isScheduled && (
                <>
                    <div className={props.classes.recepientEmail}>
                        <div className={props.classes.emailTitle}>Recipient Email</div>
                        <Group name={'searchemail'}>
                            <AutoCompleteTextField
                                placeholder={'Enter email...'}
                                handleKeyDown={handleKeyDown}
                                handleSelect={handleSelect}
                                label={``}
                                type={`email`}
                                list={users.map(u => ({name: u.email, value: u.email}))}
                                handleBlur={handleBlur}
                                valid={true}
                                name={`email`}
                                required={1}
                                value={clear}
                            />
                            <div className={props.classes.addNewEmail} >
                                <Button type={`action`} handleClick={() => handleSelect(emailInput)} variant={`normal`} name={`Add`} text={`Add`} />
                            </div>
                        </Group>
                        {formErrorText[RECIPIENTS] && <p className={props.classes.errorText}>{formErrorText[RECIPIENTS]}</p>}
                        <Group name={'emailrow'} >
                            <Stack items={convertRecipientsToObjectArr(recipients)} handleDeletion={handleRemoveRecipient}></Stack>
                        </Group>
                        <div className={props.classes.frequencySection}>
                            <div className={props.classes.frequency}>Frequency</div>
                            <Group name={'frequency'}>
                                {FREQUENCY_OPTIONS.map(i => <Control
                                    key={i.value}
                                    text={i.title}
                                    value={i.value}
                                    selected={i.value === scheduleValues[FREQUENCY]}
                                    handleSelect={handleFrequencyChange}
                                    variant={'secondary'}
                                    addCheckWhenSelected={true}
                                />)}
                            </Group>
                            <div className={props.classes.dayPickerSection}>
                                <Group name={'daypickers'}>
                                    {scheduleValues[FREQUENCY] === 'WEEKLY' &&
                                        <div className={props.classes.sendDate}>
                                            <span>Send Date</span>
                                            <SingleSelectDropDown
                                                name={'On Day'}
                                                selected={fromDatesMappedByDays.find(d => d.value === scheduleValues[START_DATE])}
                                                onSelect={handleFromDateChange}
                                                items={fromDatesMappedByDays}
                                                searchable={false}
                                                paginated={false}
                                                itemsPerPage={4}
                                                disabled={scheduleValues[FREQUENCY] != 'WEEKLY'}
                                            />
                                        </div>
                                      }
                                    {scheduleValues[END_DATE] &&
                                        <div className={props.classes.endDate}>
                                            <span>End Date</span>
                                            <DayPickerDropDown
                                                selectedDay={scheduleValues[END_DATE]}
                                                selectDay={handleToDateChange}
                                                disabledDates={getDisabledDates()} //put disable dates for past
                                                alignedRight={false}
                                            />
                                        </div>
                                    }
                                </Group>
                                <div className={props.classes.deliverySummaryMessage}>
                                    <Information message={getDateRangeMessage()}></Information>
                                </div>
                            </div>

                        </div>


                    </div>
                </>
            )
            }
        </div>
    )
});

